import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import VendorDetail from "./VendorDetail";
import { Download } from 'lucide-react';
import axiosInstance from "../../api/axiosInstance";

const PAGE_SIZE = 10;
const TOAST_DURATION = 4000;

// ── API layer ────────────────────────────────────────────────────────────────
const vendorApi = {
  list: (params = {}) => {
    const p = {};
    if (params.search) p.q = params.search;
    if (params.onboarding_stage) p.onboarding_stage = params.onboarding_stage;
    if (params.registration_step)
      p.registration_step = params.registration_step;
    if (params.invited_vendor !== "") p.invited_vendor = params.invited_vendor;
    if (params.user_is_active !== "") p.user_is_active = params.user_is_active;
    if (params.business_type) p.business_type = params.business_type;
    if (params.organization_type)
      p.organization_type = params.organization_type;
    if (params.income_proof_type)
      p.income_proof_type = params.income_proof_type;
    if (params.city_id) p.city_id = params.city_id;
    if (params.source) p.source = params.source;
    return axiosInstance.get("/api/superadmin/vendors/", { params: p });
  },

  exportCsv: (params = {}) => {
    const p = {};

    if (params.search) p.q = params.search;
    if (params.onboarding_stage)
      p.onboarding_stage = params.onboarding_stage;
    if (params.registration_step)
      p.registration_step = params.registration_step;
    if (params.source) p.source = params.source;
    if (params.business_type) p.business_type = params.business_type;
    if (params.organization_type)
      p.organization_type = params.organization_type;
    if (params.income_proof_type)
      p.income_proof_type = params.income_proof_type;

    if (params.invited_vendor !== "")
      p.invited_vendor = params.invited_vendor;
    if (params.user_is_active !== "")
      p.user_is_active = params.user_is_active;

    return axiosInstance.get(
      "/api/superadmin/vendors/export-csv/",
      {
        params: p,
        responseType: "blob",
      },
    );
  },
};

const CloseIcon = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);
const ChevronLeftIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
  >
    <polyline points="15 18 9 12 15 6" />
  </svg>
);
const ChevronRightIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
  >
    <polyline points="9 18 15 12 9 6" />
  </svg>
);
const LoaderIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    style={{ animation: "vd-spin 0.8s linear infinite" }}
  >
    <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
  </svg>
);
const UserIcon = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

let _vToastSetter = null;
const toast = {
  _push: (item) => {
    const id = Date.now() + Math.random();
    _vToastSetter?.((p) => [...p, { id, ...item }]);
    return id;
  },
  success: (title, msg) => toast._push({ type: "success", title, msg }),
  error: (title, msg) => toast._push({ type: "error", title, msg }),
  info: (title, msg) => toast._push({ type: "info", title, msg }),
  loading: (title) => toast._push({ type: "loading", title, persistent: true }),
  dismiss: (id) => _vToastSetter?.((p) => p.filter((t) => t.id !== id)),
};
const TOAST_META = {
  success: { bar: "#16a34a", icon: "✓" },
  error: { bar: "#dc2626", icon: "✕" },
  info: { bar: "#1d4ed8", icon: "i" },
  loading: { bar: "#94a3b8", icon: null },
};
const ToastItem = ({ t, onDismiss }) => {
  const meta = TOAST_META[t.type];
  const [progress, setProgress] = useState(100);
  const [exiting, setExiting] = useState(false);
  const rafRef = useRef(null);
  const startTs = useRef(Date.now());
  const dismiss = useCallback(() => {
    setExiting(true);
    setTimeout(() => onDismiss(t.id), 280);
  }, [t.id, onDismiss]);
  useEffect(() => {
    if (t.persistent) return;
    const tick = () => {
      const pct = Math.max(
        0,
        100 - ((Date.now() - startTs.current) / TOAST_DURATION) * 100,
      );
      setProgress(pct);
      if (pct > 0) rafRef.current = requestAnimationFrame(tick);
      else dismiss();
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [t.persistent, dismiss]);
  return (
    <div
      style={{
        position: "relative",
        overflow: "hidden",
        background: "#fff",
        borderRadius: 10,
        borderLeft: `4px solid ${meta.bar}`,
        boxShadow: "0 4px 20px rgba(0,0,0,.10)",
        minWidth: 300,
        maxWidth: 360,
        animation: exiting
          ? "vd-alOut .28s ease forwards"
          : "vd-alIn .28s cubic-bezier(.34,1.56,.64,1) forwards",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: 10,
          padding: "13px 14px 14px",
        }}
      >
        <div
          style={{
            flexShrink: 0,
            width: 24,
            height: 24,
            borderRadius: "50%",
            background: `${meta.bar}18`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginTop: 1,
          }}
        >
          {t.type === "loading" ? (
            <svg
              width="13"
              height="13"
              viewBox="0 0 13 13"
              fill="none"
              style={{ animation: "vd-spin .85s linear infinite" }}
            >
              <circle
                cx="6.5"
                cy="6.5"
                r="5"
                stroke={meta.bar}
                strokeWidth="2"
                strokeDasharray="8 20"
                strokeLinecap="round"
              />
            </svg>
          ) : (
            <span style={{ fontSize: 11, fontWeight: 800, color: meta.bar }}>
              {meta.icon}
            </span>
          )}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p
            style={{
              margin: 0,
              fontSize: 13.5,
              fontWeight: 650,
              color: "#0f172a",
            }}
          >
            {t.title}
          </p>
          {t.msg && (
            <p style={{ margin: "3px 0 0", fontSize: 12.5, color: "#64748b" }}>
              {t.msg}
            </p>
          )}
        </div>
        <button
          onClick={dismiss}
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            color: "#cbd5e1",
            padding: 2,
            display: "flex",
            borderRadius: 4,
          }}
          onMouseEnter={(e) => (e.currentTarget.style.color = "#94a3b8")}
          onMouseLeave={(e) => (e.currentTarget.style.color = "#cbd5e1")}
        >
          <CloseIcon />
        </button>
      </div>
      {!t.persistent && (
        <div
          style={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            height: 3,
            background: "#f1f5f9",
          }}
        >
          <div
            style={{
              height: "100%",
              width: `${progress}%`,
              background: meta.bar,
              transition: "width .1s linear",
              borderRadius: "0 2px 0 0",
            }}
          />
        </div>
      )}
    </div>
  );
};
const ToastContainer = () => {
  const [toasts, setToasts] = useState([]);
  _vToastSetter = setToasts;
  const dismiss = useCallback(
    (id) => setToasts((p) => p.filter((t) => t.id !== id)),
    [],
  );
  if (!toasts.length) return null;
  return (
    <div
      style={{
        position: "fixed",
        bottom: 24,
        right: 24,
        zIndex: 9999,
        display: "flex",
        flexDirection: "column",
        gap: 10,
        alignItems: "flex-end",
      }}
    >
      {toasts.map((t) => (
        <ToastItem key={t.id} t={t} onDismiss={dismiss} />
      ))}
    </div>
  );
};


const SkeletonRow = () => (
  <tr style={{ borderBottom: "1px solid #f1f5f9" }}>
    {[40, 200, 160, 120, 150, 120, 130, 130, 90].map((w, i) => (
      <td key={i} style={{ padding: "14px 16px" }}>
        <div
          style={{
            height: 13,
            width: w,
            maxWidth: "100%",
            borderRadius: 6,
            background:
              "linear-gradient(90deg,#f1f5f9 25%,#e2e8f0 50%,#f1f5f9 75%)",
            backgroundSize: "200% 100%",
            animation: "vd-shimmer 1.4s infinite",
          }}
        />
      </td>
    ))}
  </tr>
);
const TableSkeleton = () => (
  <>
    {Array.from({ length: 8 }).map((_, i) => (
      <SkeletonRow key={i} />
    ))}
  </>
);

const Pagination = ({ currentPage, totalPages, totalCount, onPageChange }) => {
  if (totalPages <= 1) return null;
  const startItem = (currentPage - 1) * PAGE_SIZE + 1;
  const endItem = Math.min(currentPage * PAGE_SIZE, totalCount);
  const getPages = () => {
    if (totalPages <= 7)
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    const pages = [1];
    if (currentPage > 3) pages.push("...");
    for (
      let i = Math.max(2, currentPage - 1);
      i <= Math.min(totalPages - 1, currentPage + 1);
      i++
    )
      pages.push(i);
    if (currentPage < totalPages - 2) pages.push("...");
    pages.push(totalPages);
    return pages;
  };
  const btnBase = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: 34,
    height: 34,
    padding: "0 6px",
    border: "1px solid #e2e8f0",
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 500,
    cursor: "pointer",
    background: "#fff",
    color: "#374151",
    transition: "all .15s",
  };
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "14px 20px",
        borderTop: "1px solid #f1f5f9",
        background: "#f8fafc",
        flexWrap: "wrap",
        gap: 10,
      }}
    >
      <span style={{ fontSize: 13, color: "#64748b" }}>
        Showing{" "}
        <strong style={{ color: "#374151" }}>
          {startItem}–{endItem}
        </strong>{" "}
        of <strong style={{ color: "#374151" }}>{totalCount}</strong> vendors
      </span>
      <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          style={{
            ...btnBase,
            opacity: currentPage === 1 ? 0.4 : 1,
            cursor: currentPage === 1 ? "not-allowed" : "pointer",
          }}
        >
          <ChevronLeftIcon />
        </button>
        {getPages().map((p, i) =>
          p === "..." ? (
            <span
              key={`e${i}`}
              style={{
                padding: "0 4px",
                color: "#94a3b8",
                fontSize: 13,
                lineHeight: "34px",
              }}
            >
              …
            </span>
          ) : (
            <button
              key={p}
              onClick={() => onPageChange(p)}
              className={p === currentPage ? "" : "vd-page-btn"}
              style={{
                ...btnBase,
                ...(p === currentPage
                  ? {
                    background: "#3b82f6",
                    color: "#fff",
                    border: "1px solid #3b82f6",
                    fontWeight: 600,
                  }
                  : {}),
              }}
            >
              {p}
            </button>
          ),
        )}
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          style={{
            ...btnBase,
            opacity: currentPage === totalPages ? 0.4 : 1,
            cursor: currentPage === totalPages ? "not-allowed" : "pointer",
          }}
        >
          <ChevronRightIcon />
        </button>
      </div>
    </div>
  );
};

const SOURCE_OPTIONS = [
  { value: "self_registered", label: "Self Registered" },
  { value: "csv_upload", label: "CSV Upload" },
  { value: "user_added", label: "User Added" },
  // { value: "admin_added", label: "Admin Added" },
];
const SOURCE_CONFIG = {
  self_registered: { bg: "#dcfce7", color: "#16a34a" },
  csv_upload: { bg: "#dbeafe", color: "#1d4ed8" },
  user_added: { bg: "#fef3c7", color: "#d97706" },
  // admin_added: { bg: "#ede9fe", color: "#7c3aed" },
};
const SourceBadge = ({ source }) => {
  const cfg = SOURCE_CONFIG[source] || { bg: "#f1f5f9", color: "#64748b" };
  const label =
    SOURCE_OPTIONS.find((o) => o.value === source)?.label ||
    source?.replace(/_/g, " ") ||
    "Not Specified";
  return (
    <span
      style={{
        display: "inline-block",
        padding: "3px 10px",
        borderRadius: 20,
        fontSize: 11.5,
        fontWeight: 600,
        background: cfg.bg,
        color: cfg.color,
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </span>
  );
};

const ONBOARDING_CONFIG = {
  submitted: {
    bg: "#dcfce7",
    color: "#16a34a",
    dot: "#22c55e",
    label: "Submitted",
  },
  step4_financial: {
    bg: "#fef3c7",
    color: "#d97706",
    dot: "#f59e0b",
    label: "Step 4 - Financial",
  },
  step3_documents: {
    bg: "#dbeafe",
    color: "#1d4ed8",
    dot: "#3b82f6",
    label: "Step 3 - Documents",
  },
  step2_pan: {
    bg: "#ede9fe",
    color: "#7c3aed",
    dot: "#8b5cf6",
    label: "Step 2 - PAN",
  },
  step1_gst: {
    bg: "#e0f2fe",
    color: "#0369a1",
    dot: "#38bdf8",
    label: "Step 1 - GST",
  },
};
const KYC_STATUS_CONFIG = {
  verified: { bg: "#dcfce7", color: "#16a34a", dot: "#22c55e" },
  pending: { bg: "#fef3c7", color: "#d97706", dot: "#f59e0b" },
  failed: { bg: "#fee2e2", color: "#dc2626", dot: "#f43f5e" },
  not_started: { bg: "#f1f5f9", color: "#94a3b8", dot: "#cbd5e1" },
};

const OnboardingBadge = ({ stage }) => {
  const cfg = ONBOARDING_CONFIG[stage] || {
    bg: "#f1f5f9",
    color: "#64748b",
    dot: "#94a3b8",
    label: stage?.replace(/_/g, " ") || "Not Specified",
  };
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        padding: "3px 10px",
        borderRadius: 20,
        fontSize: 11.5,
        fontWeight: 600,
        background: cfg.bg,
        color: cfg.color,
        whiteSpace: "nowrap",
      }}
    >
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          background: cfg.dot,
          flexShrink: 0,
        }}
      />
      {cfg.label}
    </span>
  );
};
const KycMiniTag = ({ status, label }) => {
  const cfg = KYC_STATUS_CONFIG[status] || KYC_STATUS_CONFIG.not_started;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        padding: "2px 7px",
        borderRadius: 6,
        fontSize: 11,
        fontWeight: 600,
        background: cfg.bg,
        color: cfg.color,
      }}
    >
      <span
        style={{
          width: 5,
          height: 5,
          borderRadius: "50%",
          background: cfg.dot,
        }}
      />
      {label}
    </span>
  );
};

const StatsBar = ({ vendors, loading }) => {


  // return (
  //   <div
  //     style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" }}
  //   >
  //     <Card
  //       label="Total Vendors"
  //       count={total}
  //       color="#6366f1"
  //       bg="#f5f3ff"
  //       border="#e0e7ff"
  //     />
  //     <Card
  //       label="Submitted"
  //       count={submitted}
  //       color="#16a34a"
  //       bg="#f0fdf4"
  //       border="#bbf7d0"
  //     />
  //     <Card
  //       label="GST Verified"
  //       count={verified}
  //       color="#2563eb"
  //       bg="#eff6ff"
  //       border="#bfdbfe"
  //     />
  //     <Card
  //       label="Active Users"
  //       count={active}
  //       color="#d97706"
  //       bg="#fffbeb"
  //       border="#fde68a"
  //     />
  //   </div>
  // );
};

const Vendor = () => {
  const [allVendors, setAllVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");
  const [filterStage, setFilterStage] = useState("");
  const [filterStep, setFilterStep] = useState("");
  const [filterBusinessType, setFilterBusinessType] = useState("");
  const [filterIncomeProof, setFilterIncomeProof] = useState("");
  const [filterActive, setFilterActive] = useState("");
  const [filterInvited, setFilterInvited] = useState("");
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [filterSource, setFilterSource] = useState("");

  const [exporting, setExporting] = useState(false);

  const handleDownloadCsv = async () => {
    setExporting(true);

    try {
      const { data } = await vendorApi.exportCsv({
        search,
        onboarding_stage: filterStage,
        registration_step: filterStep,
        business_type: filterBusinessType,
        income_proof_type: filterIncomeProof,
        user_is_active: filterActive,
        invited_vendor: filterInvited,
        source: filterSource,
      });

      const url = window.URL.createObjectURL(data);
      const link = document.createElement("a");

      link.href = url;
      link.download = "vendors.csv";
      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(url);

      toast.success("Download Complete", "Vendor CSV downloaded successfully.");
    } catch (error) {
      toast.error("Download Failed", "Unable to download vendor records.");
    } finally {
      setExporting(false);
    }
  };
  const fetchVendors = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await vendorApi.list({
        search,
        onboarding_stage: filterStage,
        registration_step: filterStep,
        business_type: filterBusinessType,
        income_proof_type: filterIncomeProof,
        user_is_active: filterActive,
        invited_vendor: filterInvited,
        source: filterSource,
      });
      // Support both { results: [] } and plain array responses
      setAllVendors(data?.results ?? data ?? []);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 403) {
        toast.error(
          "Access Denied",
          "You don't have permission to view vendors.",
        );
      } else if (!err.response) {
        toast.error("Network Error", "Unable to reach the server.");
      } else {
        toast.error(
          "Failed to Load",
          `Unable to fetch vendors (HTTP ${status}).`,
        );
      }
    } finally {
      setLoading(false);
    }
  }, [
    search,
    filterStage,
    filterStep,
    filterBusinessType,
    filterIncomeProof,
    filterActive,
    filterInvited,
    filterSource,
  ]);

  useEffect(() => {
    setCurrentPage(1);
  }, [
    search,
    filterStage,
    filterStep,
    filterBusinessType,
    filterIncomeProof,
    filterActive,
    filterInvited,
    filterSource,
  ]);
  useEffect(() => {
    const t = setTimeout(fetchVendors, 350);
    return () => clearTimeout(t);
  }, [fetchVendors]);

  const totalCount = allVendors.length;
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);
  const pagedVendors = useMemo(
    () =>
      allVendors.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE),
    [allVendors, currentPage],
  );

  const hasFilters =
    search ||
    filterStage ||
    filterStep ||
    filterBusinessType ||
    filterIncomeProof ||
    filterActive ||
    filterInvited ||
    filterSource;
  const clearFilters = () => {
    setSearch("");
    setFilterStage("");
    setFilterStep("");
    setFilterBusinessType("");
    setFilterIncomeProof("");
    setFilterActive("");
    setFilterInvited("");
    setFilterSource("")
  };

  const inputBase = {
    padding: "8px 12px",
    border: "1px solid #e2e8f0",
    borderRadius: 8,
    fontSize: 13.5,
    background: "#f8fafc",
    color: "#1e293b",
    outline: "none",
    fontFamily: "inherit",
  };
  const COLS = [
    "Sr No.",
    "Business Name",
    "Contact",
    "City",
    "Category",
    "Onboarding Stage",
    "GST / PAN",
    "Created At",
    "Source"
    // "Actions",
  ];

  return (
    <div
      style={{
        fontFamily: "'Inter', -apple-system, sans-serif",
        padding: 24,
        background: "#f8fafc",
        minHeight: "100vh",
        color: "#1e293b",
      }}
    >
      <style>{`
        @keyframes vd-shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
        @keyframes vd-fadeUp  { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:none} }
        @keyframes vd-alIn    { from{opacity:0;transform:translateX(24px) scale(.97)} to{opacity:1;transform:none} }
        @keyframes vd-alOut   { from{opacity:1} to{opacity:0;transform:translateX(28px) scale(.95)} }
        @keyframes vd-spin    { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
        .vd-row:hover         { background:#f8fafc !important; cursor:pointer; }
        .vd-view-btn:hover    { background:#dbeafe !important; }
        .vd-page-btn:hover    { background:#f1f5f9 !important; border-color:#cbd5e1 !important; }
        * { box-sizing:border-box; }
      `}</style>

      <ToastContainer />

      {selectedVendor ? (
        <VendorDetail
          vendorPreview={selectedVendor}
          onBack={() => setSelectedVendor(null)}
        />
      ) : (
        <>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              marginBottom: 24,
              flexWrap: "wrap",
              gap: 12,
            }}
          >
            <div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  marginBottom: 4,
                }}
              >
                <h1
                  style={{
                    margin: 0,
                    fontSize: 22,
                    fontWeight: 700,
                    color: "#0f172a",
                  }}
                >
                  Vendors
                </h1>
              </div>
              <p style={{ margin: 0, fontSize: 13, color: "#64748b" }}>
                Manage vendor profiles, KYC status and onboarding progress
              </p>
            </div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <button
                onClick={handleDownloadCsv}
                disabled={exporting}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "9px 18px",
                  background: "#6d57f9",
                  color: "#fff",
                  border: "none",
                  borderRadius: 8,
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: exporting ? "not-allowed" : "pointer",
                  opacity: exporting ? 0.7 : 1,
                }}
              >
                <Download />
                {exporting ? "Exporting..." : "Download CSV"}
              </button>




              <button
                onClick={fetchVendors}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "9px 18px",
                  background: "#fff",
                  color: "#374151",
                  border: "1px solid #e2e8f0",
                  borderRadius: 8,
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: "pointer",
                  boxShadow: "0 1px 3px rgba(0,0,0,.05)",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.background = "#f1f5f9")
                }
                onMouseLeave={(e) => (e.currentTarget.style.background = "#fff")}
              >
                {loading ? (
                  <LoaderIcon />
                ) : (
                  <svg
                    width="15"
                    height="15"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <polyline points="23 4 23 10 17 10" />
                    <polyline points="1 20 1 14 7 14" />
                    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
                  </svg>
                )}
                {/* Refresh */}
              </button>
            </div>
          </div>

          <StatsBar vendors={allVendors} loading={loading} />








          <div
            style={{
              // background: "#fff",
              borderRadius: 8,
              padding: "10px 14px",
              // border: "1px solid #e2e8f0",
              marginBottom: 16,
              // boxShadow: "0 1px 3px rgba(0,0,0,.04)",
              display: "flex",
              gap: 12,
              alignItems: "center",
              justifyContent: "flex-end",
              flexWrap: "wrap",
            }}
          >
            <select
              style={{ ...inputBase, cursor: "pointer", flex: "0 1 200px" }}
              value={filterSource}
              onChange={(e) => setFilterSource(e.target.value)}
            >
              <option value="">All Sources</option>
              {SOURCE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            {hasFilters && (
              <button
                onClick={clearFilters}
                style={{
                  ...inputBase,
                  background: "#fff5f5",
                  color: "#dc2626",
                  border: "1px solid #fecaca",
                  cursor: "pointer",
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                }}
              >
                Clear
              </button>
            )}
          </div>





          {/* <div
            style={{
              background: "#fff",
              borderRadius: 12,
              padding: "14px 18px",
              border: "1px solid #e2e8f0",
              marginBottom: 16,
              boxShadow: "0 1px 3px rgba(0,0,0,.04)",
            }}
          >
            <div
              style={{
                display: "flex",
                gap: 12,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <div style={{ position: "relative", flex: "1 1 220px" }}>
                <span
                  style={{
                    position: "absolute",
                    left: 10,
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: "#94a3b8",
                    pointerEvents: "none",
                  }}
                >
                  <SearchIcon />
                </span>
                <input
                  style={{
                    ...inputBase,
                    width: "100%",
                    paddingLeft: 34,
                    boxSizing: "border-box",
                  }}
                  placeholder="Search by name, email, phone…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onFocus={(e) => (e.target.style.borderColor = "#3b82f6")}
                  onBlur={(e) => (e.target.style.borderColor = "#e2e8f0")}
                />
              </div>
              <select
                style={{ ...inputBase, cursor: "pointer", flex: "0 1 170px" }}
                value={filterStage}
                onChange={(e) => setFilterStage(e.target.value)}
              >
                <option value="">All Stages</option>
                <option value="submitted">Submitted</option>
                <option value="step4_financial">Step 4 - Financial</option>
                <option value="step3_documents">Step 3 - Documents</option>
                <option value="step2_pan">Step 2 - GST&PAN</option>
                <option value="step1_gst">Step 1 - Basic</option>
              </select>
              <select
                style={{ ...inputBase, cursor: "pointer", flex: "0 1 150px" }}
                value={filterActive}
                onChange={(e) => setFilterActive(e.target.value)}
              >
                <option value="">User Status</option>
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
              {hasFilters && (
                <button
                  onClick={clearFilters}
                  style={{
                    ...inputBase,
                    background: "#fff5f5",
                    color: "#dc2626",
                    border: "1px solid #fecaca",
                    cursor: "pointer",
                    fontWeight: 600,
                    fontSize: 13,
                    whiteSpace: "nowrap",
                  }}
                >
                  Clear
                </button>
              )}
            </div>
          </div> */}

          <div
            style={{
              background: "#fff",
              borderRadius: 12,
              border: "1px solid #e2e8f0",
              overflow: "hidden",
              boxShadow: "0 1px 4px rgba(0,0,0,.05)",
            }}
          >
            <div style={{ overflowX: "auto" }}>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: 14,
                }}
              >
                <thead>
                  <tr>
                    {COLS.map((h) => (
                      <th
                        key={h}
                        style={{
                          padding: "11px 16px",
                          textAlign: "left",
                          fontSize: 12,
                          fontWeight: 600,
                          color: "#64748b",
                          textTransform: "uppercase",
                          letterSpacing: ".05em",
                          background: "#f8fafc",
                          borderBottom: "1px solid #e2e8f0",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <TableSkeleton />
                  ) : pagedVendors.length === 0 ? (
                    <tr>
                      <td colSpan={COLS.length}>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            padding: "56px 20px",
                            gap: 8,
                          }}
                        >
                          <p
                            style={{
                              margin: 0,
                              fontSize: 16,
                              fontWeight: 600,
                              color: "#374151",
                            }}
                          >
                            No vendors found
                          </p>
                          <p
                            style={{
                              margin: 0,
                              fontSize: 13,
                              color: "#94a3b8",
                            }}
                          >
                            {hasFilters
                              ? "Try adjusting your filters"
                              : "No vendors have registered yet"}
                          </p>
                          {hasFilters && (
                            <button
                              onClick={clearFilters}
                              style={{
                                marginTop: 8,
                                padding: "8px 18px",
                                background: "#3b82f6",
                                color: "#fff",
                                border: "none",
                                borderRadius: 8,
                                fontSize: 14,
                                fontWeight: 600,
                                cursor: "pointer",
                              }}
                            >
                              Clear Filters
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ) : (
                    pagedVendors.map((v, idx) => (
                      <tr
                        key={v.id}
                        className="vd-row"
                        style={{
                          borderBottom: "1px solid #f1f5f9",
                          transition: "background .12s",
                        }}
                      // onClick={() => setSelectedVendor(v)}
                      >
                        <td
                          style={{
                            padding: "13px 16px",
                            color: "#94a3b8",
                            fontSize: 13,
                          }}
                        >
                          {(currentPage - 1) * PAGE_SIZE + idx + 1}
                        </td>
                       <td
  style={{
    padding: "13px 16px",
    width: 220,
    minWidth: 220,
    maxWidth: 220,
    boxSizing: "border-box",
  }}
>
  <div
    className="hide-scrollbar"
    title={
      v.gst_response?.trade_name?.trim() ||
      v.business_name ||
      "Not specified"
    }
    onWheel={(e) => {
      const el = e.currentTarget;
      if (el.scrollWidth > el.clientWidth) {
        el.scrollLeft += e.deltaY || e.deltaX;
      }
    }}
    style={{
      fontWeight: 700,
      color: "#0f172a",
      fontSize: 13.5,
      width: "100%",
      overflowX: "auto",
      overflowY: "hidden",
      whiteSpace: "nowrap",
      scrollbarWidth: "none",
      msOverflowStyle: "none",
      cursor: "text",
    }}
  >
    {v.gst_response?.trade_name?.trim() ||
      v.business_name ||
      "Not specified"}
  </div>
</td>
                        <td style={{ padding: "13px 16px", minWidth: 180 }}>
                          <div
                            style={{
                              fontSize: 13,
                              fontWeight: 600,
                              color: "#334155",
                            }}
                          >
                            {v.contact_person_name || v.user_name || "Not Specified"}
                          </div>
                          <div
                            style={{
                              fontSize: 11.5,
                              color: "#94a3b8",
                              marginTop: 1,
                              display: "flex",
                              alignItems: "center",
                              gap: 3,
                            }}
                          >
                            <UserIcon /> {v.user_email}
                          </div>
                          <div
                            style={{
                              fontSize: 11.5,
                              color: "#94a3b8",
                              marginTop: 1,
                            }}
                          >
                            {v.user_phone}
                          </div>
                        </td>


                        {/* City */}
                        {/* <td
                          style={{
                            padding: "13px 16px",
                            color: "#475569",
                            fontSize: 13,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {v.city || v.gst_city || v.city_name || "Not specified"}
                        </td> */}

                        {/* City */}

                        <td
                          style={{
                            padding: "13px 16px",
                            color: "#475569",
                            fontSize: 13,
                            minWidth: 140,
                            verticalAlign: "middle",
                          }}
                        >
                          {(() => {
                            const city = v.gst_city || v.city_name || v.city;
                            const state = v.gst_state;

                            // GST country can contain the default "India",
                            // so show it only when GST city or state is available.
                            const country =
                              v.gst_city || v.gst_state ? v.gst_country : null;

                            const location = [city, state, country]
                              .filter(Boolean)
                              .filter(
                                (value, index, arr) =>
                                  arr.findIndex(
                                    (item) =>
                                      item.trim().toLowerCase() ===
                                      value.trim().toLowerCase()
                                  ) === index
                              );

                            if (location.length === 0) {
                              return "Not specified";
                            }

                            return (
                              <div
                                style={{
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: 4,
                                  lineHeight: 1.4,
                                }}
                              >
                                <span style={{ fontWeight: 500, color: "#475569" }}>
                                  {location[0]}
                                </span>

                                {location.length > 1 && (
                                  <span
                                    style={{
                                      fontSize: 12,
                                      color: "#94a3b8",
                                    }}
                                  >
                                    {location.slice(1).join(", ")}
                                  </span>
                                )}
                              </div>
                            );
                          })()}
                        </td>
                        {/* Category */}
                        <td
                          style={{
                            padding: "13px 16px",
                            color: "#475569",
                            fontSize: 13,
                            minWidth: 150,
                          }}
                        >
                          {Array.isArray(v.selected_categories) &&
                            v.selected_categories.length > 0
                            ? v.selected_categories
                              .map((category) =>
                                typeof category === "string"
                                  ? category
                                  : category.name || category.category_name || "",
                              )
                              .filter(Boolean)
                              .join(", ") || "Not specified"
                            : Array.isArray(v.categories) && v.categories.length > 0
                              ? v.categories
                                .map((category) =>
                                  typeof category === "string"
                                    ? category
                                    : category.name || category.category_name || "",
                                )
                                .filter(Boolean)
                                .join(", ") || "Not specified"
                              : "Not specified"}
                        </td>
                        <td style={{ padding: "13px 16px" }}>
                          <OnboardingBadge stage={v.onboarding_stage} />
                          <div
                            style={{
                              fontSize: 11,
                              color: "#94a3b8",
                              marginTop: 4,
                            }}
                          >
                            Step {v.registration_step}
                          </div>
                        </td>
                        <td style={{ padding: "13px 16px" }}>
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 4,
                            }}
                          >
                            <KycMiniTag
                              status={v.gst_status}
                              label={`GST: ${v.gst_status?.replace("_", " ") || "Not Specified"}`}
                            />
                            <KycMiniTag
                              status={v.pan_status}
                              label={`PAN: ${v.pan_status?.replace("_", " ") || "Not Specified"}`}
                            />
                          </div>
                        </td>
                        {/* <td
                          style={{
                            padding: "13px 16px",
                            color: "#475569",
                            fontSize: 13,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {v.created_at
                            ? new Date(v.created_at).toLocaleString()
                            : "-"}
                        </td> */}

                        <td
                          style={{
                            padding: "13px 16px",
                            color: "#475569",
                            fontSize: 13,
                            minWidth: 130,
                            verticalAlign: "middle",
                          }}
                        >
                          {v.created_at ? (
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 4,
                                lineHeight: 1.4,
                              }}
                            >
                              <span style={{ whiteSpace: "nowrap", fontWeight: 500 }}>
                                {new Date(v.created_at).toLocaleDateString("en-IN")}
                              </span>

                              <span
                                style={{
                                  whiteSpace: "nowrap",
                                  fontSize: 12,
                                  color: "#94a3b8",
                                }}
                              >
                                {new Date(v.created_at).toLocaleTimeString("en-IN", {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                  hour12: true,
                                })}
                              </span>
                            </div>
                          ) : (
                            "-"
                          )}
                        </td>



                        {/* <td
                          style={{ padding: "13px 16px" }}
                          onClick={(e) => e.stopPropagation()}
                        > */}
                        {/* <button
                            className="vd-view-btn"
                            title="View Details"
                            onClick={() => setSelectedVendor(v)}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              padding: "6px 12px",
                              gap: 5,
                              background: "#eff6ff",
                              border: "1px solid #bfdbfe",
                              borderRadius: 6,
                              cursor: "pointer",
                              color: "#3b82f6",
                              fontSize: 12,
                              fontWeight: 600,
                              transition: "background .15s",
                              whiteSpace: "nowrap",
                            }}
                          >
                            <EyeIcon /> View
                          </button> */}
                        {/* </td> */}
                        <td style={{ padding: "13px 16px" }}>
                          <SourceBadge source={v.source} />
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            {!loading && allVendors.length > 0 && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalCount={totalCount}
                onPageChange={(p) => {
                  if (p >= 1 && p <= totalPages) setCurrentPage(p);
                }}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default Vendor;
